import React, { useEffect, useMemo, useState } from "react";
import {
  JK_N, Pt, cardRank, cardSuit, jkBasePt, jkHomePt, jkMarblePt, jkPreview, jkRot, jkTrackPt, jkViewTurns,
} from "@samra/shared";
import type { BoardProps } from "../pages/room";
import { t } from "../i18n";
import { Button, cx } from "../ui";
import { SEAT_COLORS } from "./colors";
import { sfx, soundOn, steps } from "../sound";

/*
 * Jackaroo board. All geometry comes from @samra/shared (layout.ts), the same file the iPhone app uses:
 * this file only rotates the board so your side is at the bottom (rotation, never a mirror) and draws it.
 * The server's legal-move list is the only source of what can be played; the board never invents a move.
 */

const RANK = ["", "A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];
const SUIT = ["♠", "♥", "♦", "♣"];
const isRed = (c: number) => cardSuit(c) === 1 || cardSuit(c) === 2;
type Ref = { s: number; i: number };
const same = (a: any, b: any) => !!a && !!b && a.s === b.s && a.i === b.i;
const key = (r: Ref) => `${r.s}-${r.i}`;
const fmt = (p: Pt) => `${p[0].toFixed(3)} ${p[1].toFixed(3)}`;

export function Card({ id, onClick, state, small }: { id: number; onClick?: () => void; state?: "on" | "off" | "dim"; small?: boolean }) {
  const r = cardRank(id);
  const s = cardSuit(id);
  return (
    <button
      type="button"
      className={cx("card", isRed(id) && "is-red", state === "on" && "is-on", state === "dim" && "is-dim", small && "is-small")}
      onClick={onClick}
      disabled={!onClick}
      aria-pressed={onClick ? state === "on" : undefined}
      aria-label={`${RANK[r]}${SUIT[s]} — ${t.jkCardPower(r)}`}
    >
      <span className="card-corner" aria-hidden="true"><b>{RANK[r]}</b><i>{SUIT[s]}</i></span>
      <span className="card-pip" aria-hidden="true">{SUIT[s]}</span>
      <span className="card-power" aria-hidden="true">{t.jkCardPower(r)}</span>
    </button>
  );
}

/** Marble trail for the last move: drawn once per server event batch, then fades. */
function useTrail(events: BoardProps["events"], view: any, R: (p: Pt) => Pt) {
  const [trail, setTrail] = useState<{ id: number; lines: { a: Pt; b: Pt; c: string }[]; bursts: { p: Pt; c: string; kind: string }[] } | null>(null);
  useEffect(() => {
    const list: any[] = events.list ?? [];
    const lines: { a: Pt; b: Pt; c: string }[] = [];
    const bursts: { p: Pt; c: string; kind: string }[] = [];
    for (const e of list) {
      if (e.type === "moved") {
        const bp = view.boardPos[e.owner];
        lines.push({ a: R(jkMarblePt(bp, e.from, e.marble)), b: R(jkMarblePt(bp, e.to, e.marble)), c: SEAT_COLORS[e.owner] });
        bursts.push({ p: R(jkMarblePt(bp, e.to, e.marble)), c: SEAT_COLORS[e.owner], kind: "land" });
      }
      if (e.type === "exited") bursts.push({ p: R(jkMarblePt(view.boardPos[e.owner], 0, e.marble)), c: SEAT_COLORS[e.owner], kind: "land" });
      if (e.type === "captured") bursts.push({ p: R(jkBasePt(view.boardPos[e.victim], e.marble)), c: SEAT_COLORS[e.victim], kind: "hit" });
      if (e.type === "swapped") {
        for (const r of [e.a, e.b]) {
          const p = view.marbles[r.s]?.[r.i];
          if (p !== undefined) bursts.push({ p: R(jkMarblePt(view.boardPos[r.s], p, r.i)), c: SEAT_COLORS[r.s], kind: "land" });
        }
      }
    }
    if (!lines.length && !bursts.length) return;
    setTrail({ id: events.seq, lines, bursts });
    const id = setTimeout(() => setTrail(null), 1600);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events.seq]);
  return trail;
}

export function JackarooBoard({ view, seat, names, myTurn, move, events, busy, connected }: BoardProps) {
  const [card, setCard] = useState<number | null>(null);
  const [focus, setFocus] = useState<Ref | null>(null);
  const [split, setSplit] = useState<{ marble: Ref; steps?: number } | null>(null);
  const legal: any[] = view.legal ?? [];
  const rank = card !== null ? cardRank(card) : null;
  const n: number = view.n;
  const teams: boolean = view.teams;
  const me = seat >= 0 && seat < n;
  const myBp = me ? view.boardPos[seat] : 0;
  const turns = jkViewTurns(myBp);
  const R = (p: Pt) => jkRot(p, turns);
  const canAct = myTurn && !busy;

  // A new server state or a lost turn clears any half-made choice.
  useEffect(() => {
    setFocus(null);
    setSplit(null);
    if (!myTurn || (card !== null && !view.hand.includes(card))) setCard(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, myTurn]);

  const options = useMemo(
    () => (rank === null ? [] : legal.filter((m) => m.type === "play" && cardRank(m.card) === rank).map((m) => ({ ...m, card }))),
    [legal, rank, card],
  );
  const homeAll = (s: number) => view.marbles[s].every((p: number) => p >= 76);
  const controlled = me && teams && homeAll(seat) ? (seat + 2) % 4 : seat;
  const exitOpt = options.find((m) => m.action === "exit");
  const exitRef: Ref | null = exitOpt ? { s: controlled, i: view.marbles[controlled].indexOf(-1) } : null;

  const isSeven = rank === 7;
  const splitOpts = isSeven ? options.filter((m) => m.action === "split") : [];
  const wholeOpts = splitOpts.filter((m) => m.parts.length === 1);
  const pairOpts = splitOpts.filter((m) => m.parts.length === 2);
  const firstChoices = (mb: Ref) => [...new Set(pairOpts.filter((m) => same(m.parts[0].marble, mb)).map((m) => m.parts[0].steps as number))].sort((a, b) => a - b);
  const firstMarbles = useMemo(() => {
    const seen = new Map<string, Ref>();
    for (const m of splitOpts) seen.set(key(m.parts[0].marble), m.parts[0].marble);
    return [...seen.values()];
  }, [splitOpts]);
  const secondOpts = split && split.steps !== undefined ? pairOpts.filter((m) => same(m.parts[0].marble, split.marble) && m.parts[0].steps === split.steps) : [];

  // Which marble a move is "about": the one you tap to pick it.
  const anchor = (m: any): Ref | null => (m.action === "exit" ? exitRef : m.action === "split" ? m.parts[0].marble : m.marble);
  const movesFor = (mb: Ref) => options.filter((m) => m.action !== "split" && same(anchor(m), mb));

  const play = (m: any) => {
    if (!canAct) return;
    move(m);
    setCard(null);
    setFocus(null);
    setSplit(null);
  };
  const pickCard = (c: number) => {
    if (!canAct) return;
    if (card !== c) sfx.cardPick();
    setCard(card === c ? null : c);
    setFocus(null);
    setSplit(null);
  };

  const tapMarble = (mb: Ref) => {
    if (!canAct || card === null) return;
    if (isSeven) {
      if (!split) return firstMarbles.some((x) => same(x, mb)) ? setSplit({ marble: mb }) : undefined;
      if (split.steps === undefined) return same(split.marble, mb) ? setSplit(null) : undefined;
      const hit = secondOpts.find((m) => same(m.parts[1].marble, mb));
      if (hit) play(hit);
      return;
    }
    // J: after picking your marble, tapping another marble is the swap target.
    if (focus && rank === 11) {
      const hit = options.find((m) => m.action === "swap" && same(m.marble, focus) && same(m.target, mb));
      if (hit) return play(hit);
    }
    if (same(focus, mb)) return setFocus(null);
    const mine = movesFor(mb);
    if (mine.length === 1 && rank !== 11) return play(mine[0]);
    if (mine.length) setFocus(mb);
  };

  const tappable = (mb: Ref): boolean => {
    if (!canAct || card === null) return false;
    if (isSeven) {
      if (!split) return firstMarbles.some((x) => same(x, mb));
      if (split.steps === undefined) return same(split.marble, mb);
      return secondOpts.some((m) => same(m.parts[1].marble, mb));
    }
    if (focus && rank === 11) return options.some((m) => m.action === "swap" && same(m.marble, focus) && same(m.target, mb)) || same(focus, mb);
    return movesFor(mb).length > 0;
  };

  // Destinations to preview: the focused marble's choices, or every single-choice move once a card is picked.
  const previews = useMemo(() => {
    if (!canAct || card === null) return [] as { to: Pt; color: string; move: any; label: string; strong: boolean }[];
    const list: any[] = isSeven
      ? split?.steps !== undefined
        ? secondOpts
        : split
          ? wholeOpts.filter((m) => same(m.parts[0].marble, split.marble))
          : []
      : focus
        ? rank === 11 ? [] : movesFor(focus)
        : options.filter((m) => m.action !== "swap");
    const out: { to: Pt; color: string; move: any; label: string; strong: boolean }[] = [];
    for (const m of list) {
      for (const pv of jkPreview(view, seat, m)) {
        if (isSeven && split?.steps !== undefined && same(pv.ref, split.marble)) continue;
        out.push({ to: R(jkMarblePt(view.boardPos[pv.ref.s], pv.to, pv.ref.i)), color: SEAT_COLORS[pv.ref.s], move: m, label: m.action === "move" ? String(m.steps) : m.action === "back" ? "−4" : "", strong: !!focus || !!split });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAct, card, focus, split, options, view]);

  const trail = useTrail(events, view, R);

  // Display-only hop animation: the real positions are already in `view`; this walks each moved marble
  // square by square (so you can see it go the right way round) with a tock per square.
  const [hops, setHops] = useState<Record<string, number[]> | null>(null);
  const [hopI, setHopI] = useState(0);
  useEffect(() => {
    const moved = (events.list ?? []).filter((e: any) => e.type === "moved");
    if (!moved.length) return;
    const paths: Record<string, number[]> = {};
    let longest = 0;
    for (const e of moved) {
      const seq: number[] = [];
      if (e.back) for (let k = 0; k <= 4; k++) seq.push((e.from - k + 76) % 76);
      else for (let p = e.from; p <= e.to; p++) seq.push(p);
      paths[`${e.owner}-${e.marble}`] = seq;
      longest = Math.max(longest, seq.length - 1);
    }
    const reduce = !!window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (soundOn()) setTimeout(() => (reduce ? sfx.place() : steps(longest, 90)), 110);
    if (reduce || longest < 1) return;
    setHops(paths);
    setHopI(0);
    let i = 0;
    const id = setInterval(() => {
      i++;
      if (i >= longest) {
        clearInterval(id);
        setHops(null);
      } else setHopI(i);
    }, 90);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events.seq]);
  const shownPos = (s: number, i: number, p: number) => {
    const seq = hops?.[`${s}-${i}`];
    return seq ? seq[Math.min(hopI, seq.length - 1)] : p;
  };

  // A short "your turn" flash when the turn comes (back) to you.
  const [flash, setFlash] = useState(0);
  const wasMine = React.useRef(myTurn);
  useEffect(() => {
    if (myTurn && !wasMine.current) {
      setFlash((f) => f + 1);
      try { navigator.vibrate?.(25); } catch { /* not supported */ }
    }
    wasMine.current = myTurn;
  }, [myTurn]);
  useEffect(() => {
    if (!flash) return;
    const id = setTimeout(() => setFlash(0), 1300);
    return () => clearTimeout(id);
  }, [flash]);

  // What to say under the hand.
  const handHint = (() => {
    if (!me) return "";
    if (busy) return t.jkSending;
    if (!myTurn) return t.turnOf(names[view.turn] ?? "");
    if (view.mustDiscard) return t.mustDiscard;
    if (card === null) return controlled !== seat ? `${t.jkPlayingPartner} · ${t.pickCard}` : t.pickCard;
    if (isSeven) {
      if (!split) return t.splitPickFirst;
      if (split.steps === undefined) return t.splitHowMany;
      return t.splitSecond(7 - split.steps);
    }
    if (focus && rank === 11) return t.jkPickTarget;
    if (focus) return t.jkPickDest;
    if (exitOpt && options.length === 1) return t.jkExitHint;
    return t.jkTapMarble;
  })();

  const label = (m: any) => {
    const mn = (mb: any) => `${mb.s === seat ? "" : names[mb.s] + " "}#${mb.i + 1}`;
    switch (m.action) {
      case "exit": return t.exit;
      case "move": return `${mn(m.marble)} · ${t.stepsN(m.steps)}`;
      case "back": return `${mn(m.marble)} · ${t.back4}`;
      case "swap": return `${t.swap} ${mn(m.marble)} ⇄ ${mn(m.target)}`;
      case "split": return m.parts.map((p: any) => `${mn(p.marble)}: ${p.steps}`).join(" + ");
    }
    return "";
  };
  // Text fallback for every choice (screen readers, keyboards, and anyone who prefers buttons).
  const listed = !canAct || card === null || view.mustDiscard || isSeven ? [] : focus ? (rank === 11 ? options.filter((m) => same(m.marble, focus)) : movesFor(focus)) : options;

  /* ── drawing ── */
  const track = Array.from({ length: 76 }, (_, a) => R(jkTrackPt(a)));
  const road = `M${track.map(fmt).join("L")}Z`;
  const seatOfSide = (side: number) => view.boardPos.indexOf(side);
  const chevrons: { p: Pt; ang: number }[] = [];
  for (let a = 0; a < 76; a++) {
    if (a % 19 !== 6 && a % 19 !== 12) continue;
    const p = R(jkTrackPt(a));
    const q = R(jkTrackPt((a + 1) % 76));
    chevrons.push({ p: [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2], ang: (Math.atan2(q[1] - p[1], q[0] - p[0]) * 180) / Math.PI });
  }
  // Name plates sit next to each base, upright for every viewer: above/below it for the top and bottom
  // seats, and above it (narrower) for the side seats so they never cover a base or a home lane.
  const plateOf = (bp: number) => {
    const base = [0, 1, 2, 3].map((i) => R(jkBasePt(bp, i)));
    const cx = base.reduce((a, p) => a + p[0], 0) / 4;
    const cy = base.reduce((a, p) => a + p[1], 0) / 4;
    const v = (bp + turns) % 4; // 0 bottom, 1 right, 2 top, 3 left (as seen)
    if (v === 0) return { p: [cx, cy - 2.3] as Pt, w: 6.2, side: false };
    if (v === 2) return { p: [cx, cy + 2.3] as Pt, w: 6.2, side: false };
    return { p: [cx, cy - 2.6] as Pt, w: 4.5, side: true };
  };
  const turnPlate = plateOf(view.boardPos[view.turn] ?? 0).p;
  const turnAng = (Math.atan2(turnPlate[1] - JK_N / 2, turnPlate[0] - JK_N / 2) * 180) / Math.PI;

  return (
    <div className={cx("jackaroo", busy && "is-busy")} data-my-board-pos={myBp} data-seat={seat} data-turn={view.turn} data-over={view.over ? "1" : "0"}>
      <div className="jk-stage">
        <svg viewBox={`-0.9 -0.9 ${JK_N + 1.8} ${JK_N + 1.8}`} className="jk-board" role="group" aria-label={`${t.game.jackaroo} — ${t.jkDirection}`}>
          <defs>
            <pattern id="jk-sadu" width="1.2" height="0.9" patternUnits="userSpaceOnUse">
              <rect width="1.2" height="0.9" fill="#120b33" />
              <path d="M0 0.9 L0.3 0.3 L0.6 0.9 Z" fill="#ff6b5b" opacity="0.6" />
              <path d="M0.6 0.9 L0.9 0.3 L1.2 0.9 Z" fill="#fff4e0" opacity="0.4" />
              <rect y="0" width="1.2" height="0.12" fill="#f7b32b" opacity="0.9" />
            </pattern>
            <radialGradient id="jk-field" cx="50%" cy="45%" r="70%">
              <stop offset="0" stopColor="#3a2c92" />
              <stop offset="1" stopColor="#1d1452" />
            </radialGradient>
            <radialGradient id="jk-hole" cx="50%" cy="40%" r="60%">
              <stop offset="0" stopColor="#0e0828" />
              <stop offset="1" stopColor="#24196a" />
            </radialGradient>
            {SEAT_COLORS.map((c, i) => (
              <radialGradient key={i} id={`jk-m${i}`} cx="35%" cy="30%" r="75%">
                <stop offset="0" stopColor="#fff" stopOpacity="0.85" />
                <stop offset="0.28" stopColor={c} />
                <stop offset="1" stopColor={c} stopOpacity="0.75" />
              </radialGradient>
            ))}
          </defs>

          {/* frame: a Sadu weave band around the board */}
          <rect x="-0.9" y="-0.9" width={JK_N + 1.8} height={JK_N + 1.8} rx="1.4" fill="url(#jk-sadu)" />
          <rect x="-0.35" y="-0.35" width={JK_N + 0.7} height={JK_N + 0.7} rx="1" fill="url(#jk-field)" stroke="#f7b32b" strokeOpacity="0.55" strokeWidth="0.06" />

          {/* the road and its direction */}
          <path d={road} fill="none" stroke="#120b33" strokeOpacity="0.55" strokeWidth="1.02" strokeLinejoin="round" />
          <path d={road} fill="none" stroke="#fff4e0" strokeOpacity="0.07" strokeWidth="0.86" strokeLinejoin="round" />
          {chevrons.map((c, i) => (
            <path key={i} d="M-0.15 -0.2 L0.1 0 L-0.15 0.2" transform={`translate(${fmt(c.p)}) rotate(${c.ang})`} fill="none" stroke="#fff4e0" strokeOpacity="0.7" strokeWidth="0.08" strokeLinecap="round" strokeLinejoin="round" />
          ))}

          {/* per-seat zones: home lane (with its channel from the last square), base pocket, start square */}
          {view.boardPos.map((bp: number, s: number) => {
            const col = SEAT_COLORS[s];
            const lane = [R(jkMarblePt(bp, 75, 0)), ...[0, 1, 2, 3].map((h) => R(jkHomePt(bp, h)))];
            const base = [0, 1, 2, 3].map((i) => R(jkBasePt(bp, i)));
            const bx = Math.min(...base.map((p) => p[0])) - 0.75;
            const by = Math.min(...base.map((p) => p[1])) - 0.75;
            const bw = Math.max(...base.map((p) => p[0])) - bx + 0.75;
            const bh = Math.max(...base.map((p) => p[1])) - by + 0.75;
            const st = R(jkMarblePt(bp, 0, 0));
            const st1 = R(jkMarblePt(bp, 1, 0));
            const ang = (Math.atan2(st1[1] - st[1], st1[0] - st[0]) * 180) / Math.PI;
            return (
              <g key={`z${s}`} className="jk-zone">
                <path d={`M${lane.map(fmt).join("L")}`} fill="none" stroke={col} strokeOpacity="0.32" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
                {lane.slice(1).map((p, h) => (
                  <circle key={h} cx={p[0]} cy={p[1]} r="0.36" fill="url(#jk-hole)" stroke={col} strokeWidth={h === 3 ? 0.11 : 0.07} />
                ))}
                <rect x={bx} y={by} width={bw} height={bh} rx="0.55" fill={col} fillOpacity="0.12" stroke={col} strokeOpacity="0.55" strokeWidth="0.06" />
                {base.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="0.36" fill="url(#jk-hole)" />)}
                <circle cx={st[0]} cy={st[1]} r="0.5" fill={col} fillOpacity="0.25" stroke={col} strokeWidth="0.1" />
                <path d="M-0.16 -0.2 L0.12 0 L-0.16 0.2" transform={`translate(${fmt(st)}) rotate(${ang}) translate(0.62 0)`} fill="none" stroke={col} strokeWidth="0.09" strokeLinecap="round" strokeLinejoin="round" />
              </g>
            );
          })}

          {/* holes */}
          {track.map((p, a) => (a % 19 === 0 && seatOfSide(a / 19) >= 0 ? null : <circle key={a} cx={p[0]} cy={p[1]} r="0.33" fill="url(#jk-hole)" stroke="#fff4e0" strokeOpacity="0.16" strokeWidth="0.04" />))}

          {/* name plates, upright for every viewer */}
          {view.boardPos.map((bp: number, s: number) => {
            const { p, w, side } = plateOf(bp);
            const isTurn = s === view.turn && !view.over;
            const tag = s !== seat && teams && me && s === (seat + 2) % 4 ? t.jkPartner : "";
            const offline = connected && connected[s] === false;
            const max = side ? 8 : 12;
            const nm = (names[s] ?? "").length > max ? `${(names[s] ?? "").slice(0, max - 1)}…` : names[s] ?? "";
            return (
              <g key={`p${s}`} className={cx("jk-plate", isTurn && "is-turn", offline && "is-offline")} transform={`translate(${fmt(p)})`}>
                <rect x={-w / 2} y="-0.68" width={w} height="1.36" rx="0.68" fill="#120b33" fillOpacity="0.85" stroke={isTurn ? "#f7b32b" : SEAT_COLORS[s]} strokeWidth={isTurn ? 0.12 : 0.05} />
                <circle cx={-w / 2 + 0.62} cy="0" r="0.3" fill={SEAT_COLORS[s]} />
                {offline && <circle cx={-w / 2 + 0.62} cy="0" r="0.13" fill="#120b33" />}
                <text x="0.3" y="-0.06" className="jk-plate-name" fontSize="0.5" fill="#fff4e0" fontWeight="700" textAnchor="middle">{nm}</text>
                <text x="0.3" y="0.46" fontSize={side ? 0.32 : 0.36} fill="#fff4e0" fillOpacity="0.75" textAnchor="middle">
                  {[tag, offline ? t.jkOffline : t.jkCards(view.handCounts[s] ?? 0)].filter(Boolean).join(" · ")}
                </text>
              </g>
            );
          })}

          {/* centre: discard pile, deck, and whose turn it is */}
          <g transform={`translate(${JK_N / 2} ${JK_N / 2})`} className="jk-centre">
            {!view.over && (
              <g transform={`rotate(${turnAng})`} className="jk-turn-arrow">
                <path d="M2.2 -0.42 L3.15 0 L2.2 0.42 Z" fill="#f7b32b" />
              </g>
            )}
            <circle r="2.05" fill="#120b33" fillOpacity="0.45" stroke="#fff4e0" strokeOpacity="0.12" strokeWidth="0.05" />
            {view.topDiscard !== null ? (
              <g transform="translate(-0.95 -1.35) rotate(-6 0.95 1.35)">
                <rect width="1.9" height="2.7" rx="0.22" fill="#fff4e0" stroke="#120b33" strokeWidth="0.05" />
                <text x="0.95" y="1.25" fontSize="0.95" textAnchor="middle" fontWeight="700" fill={isRed(view.topDiscard) ? "#d93a2b" : "#1a1240"}>{RANK[cardRank(view.topDiscard)]}</text>
                <text x="0.95" y="2.2" fontSize="0.75" textAnchor="middle" fill={isRed(view.topDiscard) ? "#d93a2b" : "#1a1240"}>{SUIT[cardSuit(view.topDiscard)]}</text>
              </g>
            ) : (
              <rect x="-0.95" y="-1.35" width="1.9" height="2.7" rx="0.22" fill="none" stroke="#fff4e0" strokeOpacity="0.3" strokeWidth="0.05" strokeDasharray="0.2 0.15" />
            )}
            <text y="1.95" fontSize="0.36" textAnchor="middle" fill="#fff4e0" fillOpacity="0.7">{t.jkDeck(view.deckCount ?? 0)}</text>
          </g>

          {/* last move */}
          {trail && (
            <g key={trail.id} className="jk-trail" aria-hidden="true">
              {trail.lines.map((l, i) => <line key={i} x1={l.a[0]} y1={l.a[1]} x2={l.b[0]} y2={l.b[1]} stroke={l.c} strokeWidth="0.16" strokeLinecap="round" strokeDasharray="0.05 0.3" />)}
              {trail.bursts.map((b, i) => <circle key={i} className={`jk-burst is-${b.kind}`} cx={b.p[0]} cy={b.p[1]} r="0.5" fill="none" stroke={b.kind === "hit" ? "#ff6b5b" : b.c} strokeWidth="0.12" />)}
            </g>
          )}

          {/* where the move would land */}
          {previews.map((pv, i) => (
            <g key={`d${i}`} className={cx("jk-dest", pv.strong && "is-strong")} transform={`translate(${fmt(pv.to)})`}
              onClick={pv.strong ? () => play(pv.move) : undefined} role={pv.strong ? "button" : undefined} aria-label={pv.strong ? label(pv.move) : undefined}>
              <circle r="0.5" fill={pv.color} fillOpacity={pv.strong ? 0.35 : 0.14} stroke={pv.color} strokeWidth="0.08" strokeDasharray="0.16 0.1" />
              {pv.strong && pv.label && <text y="0.17" fontSize="0.45" textAnchor="middle" fill="#fff4e0" fontWeight="700">{pv.label}</text>}
            </g>
          ))}

          {/* marbles */}
          {view.marbles.map((arr: number[], s: number) =>
            arr.map((p, i) => {
              const mb = { s, i };
              const [x, y] = R(jkMarblePt(view.boardPos[s], shownPos(s, i, p), i));
              const can = tappable(mb);
              const chosen = same(focus, mb) || same(split?.marble, mb);
              const onTap = can ? () => tapMarble(mb) : undefined;
              return (
                <g
                  key={`m${s}${i}`}
                  className={cx("marble", can && "is-legal", chosen && "is-focus")}
                  style={{ transform: `translate(${x}px, ${y}px)` }}
                  onClick={onTap}
                  role={can ? "button" : undefined}
                  tabIndex={can ? 0 : undefined}
                  onKeyDown={can ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onTap!()) : undefined}
                  aria-label={can ? `${names[s] ?? ""} #${i + 1}` : undefined}
                  data-marble={`${s}-${i}`}
                  data-pos={p}
                >
                  {can && <circle r="0.68" className="marble-ring" fill="none" stroke="#fff4e0" strokeWidth="0.09" />}
                  {chosen && <circle r="0.7" fill="none" stroke="#f7b32b" strokeWidth="0.14" />}
                  <circle r="0.44" fill="#0b0622" opacity="0.45" transform="translate(0.06 0.09)" />
                  <circle r="0.42" fill={`url(#jk-m${s})`} stroke="#120b33" strokeWidth="0.06" />
                  <text y="0.15" fontSize="0.42" textAnchor="middle" fill="#120b33" fontWeight="700" pointerEvents="none">{i + 1}</text>
                </g>
              );
            }),
          )}
        </svg>
        {flash > 0 && <div key={flash} className="jk-turn-flash" aria-hidden="true">{t.jkYourTurnNow}</div>}
      </div>

      {me && (
        <div className={cx("jk-tray", myTurn && "is-mine")}>
          <p className={cx("jk-hint", myTurn && "is-mine", busy && "is-busy")} role="status" aria-live="polite">
            {busy && <span className="jk-spin" aria-hidden="true" />}
            {handHint}
          </p>
          <div className="jk-hand" dir="ltr" role="group" aria-label={t.jkYourCards}>
            {view.hand.map((c: number) => {
              const playable = legal.some((m) => m.type === "play" && cardRank(m.card) === cardRank(c));
              const usable = canAct && (playable || view.mustDiscard);
              return <Card key={c} id={c} state={card === c ? "on" : myTurn && !usable ? "dim" : undefined} onClick={usable ? () => pickCard(c) : undefined} />;
            })}
          </div>
          <div className="board-controls jk-options">
            {canAct && view.mustDiscard && (
              <Button disabled={card === null} onClick={() => card !== null && play({ type: "discard", card })}>{card === null ? t.discard : `${t.discard} ${RANK[cardRank(card)]}${SUIT[cardSuit(card)]}`}</Button>
            )}
            {canAct && isSeven && split && split.steps === undefined && (
              <>
                {firstChoices(split.marble).map((x) => <Button key={x} kind="ghost" onClick={() => setSplit({ marble: split.marble, steps: x })}>{t.stepsN(x)}</Button>)}
                {wholeOpts.some((m) => same(m.parts[0].marble, split.marble)) && (
                  <Button kind="ghost" onClick={() => play(wholeOpts.find((m) => same(m.parts[0].marble, split.marble)))}>{t.splitWhole}</Button>
                )}
              </>
            )}
            {listed.slice(0, 12).map((m, i) => <Button key={i} kind="ghost" onClick={() => play(m)}>{label(m)}</Button>)}
            {canAct && card !== null && !view.mustDiscard && (
              <Button kind="quiet" onClick={() => (split?.steps !== undefined ? setSplit({ marble: split.marble }) : split ? setSplit(null) : focus ? setFocus(null) : setCard(null))}>
                {split || focus ? t.splitBack : t.cancel}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
